/* ============================================================================
   mz-tracking.js — customer touchpoint attribution + journey, on the ORDER.

   Records every touch and makes it land where you can use it:
   • First-touch + last-touch attribution (UTM / referrer / landing / click-ids)
     kept in localStorage across sessions.
   • A per-session journey trail (pages, sections seen, searches) in sessionStorage.
   • Flushes attribution + journey to CART ATTRIBUTES (debounced /cart/update.js)
     so they appear on the Shopify order's "Additional details" and exports.
   • window.mzTrack.props(extra) → line-item properties for the next add-to-cart
     (consumed by mz-add-to-cart.js), so each ordered item knows where it came from.
   • window.mzTrack.emit(name,payload) → a 'mz:track' DOM event other scripts can
     listen to. The GA4/Meta mirroring runs in a Shopify Web Pixel (Customer events),
     which reads these same attributes/properties off the standard checkout events.

   Server context is provided by window.MZ_CTX (printed in theme.liquid).
   ========================================================================== */
(function () {
  'use strict';
  if (window.mzTrack) return;

  // NEVER run inside the theme editor: no beacons, no /cart writes, no journey
  // pollution — and nothing here can interfere with the editor preview. mzTrack
  // becomes a safe no-op so callers (mz-add-to-cart) work without branching.
  if (window.Shopify && window.Shopify.designMode) {
    var noop = function () {}, empty = function () { return {}; };
    window.mzTrack = { props: empty, attributes: empty, flush: noop, emit: noop, send: noop, getAttr: function () { return null; }, getJourney: function () { return null; } };
    return;
  }

  var CTX = window.MZ_CTX || {};
  var LS = 'mz_attr', SS = 'mz_journey';
  function iso() { try { return new Date().toISOString(); } catch (e) { return ''; } }
  function lget(k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } }
  function lset(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function sget(k) { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } }
  function sset(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }

  var qp;
  try { qp = new URLSearchParams(location.search); } catch (e) { qp = { get: function () { return ''; } }; }
  function q(k) { return qp.get(k) || ''; }

  /* ---------- Anonymous visitor + session ids (for funnel joins) ---------- */
  function rid() {
    try { if (crypto && crypto.getRandomValues) { var a = new Uint32Array(2); crypto.getRandomValues(a); return a[0].toString(36) + a[1].toString(36); } } catch (e) {}
    return (Date.now().toString(36) + Math.floor(Math.random() * 1e9).toString(36));
  }
  var visitorId = lget('mz_vid'); if (!visitorId) { visitorId = rid(); lset('mz_vid', visitorId); }
  var sessionId = sget('mz_sid'), isNewSession = false;
  if (!sessionId) { sessionId = rid(); sset('mz_sid', sessionId); isNewSession = true; }
  var visitNum = lget('mz_visits') || 0;
  if (isNewSession) { visitNum = visitNum + 1; lset('mz_visits', visitNum); }
  else if (!visitNum) { visitNum = 1; lset('mz_visits', 1); } // session predates the counter — this IS a visit
  var firstTs = lget('mz_first_ts'); if (!firstTs) { firstTs = Date.now(); lset('mz_first_ts', firstTs); }
  var tenureDays = Math.floor((Date.now() - firstTs) / 86400000);
  var pageId = rid(); // one id per page load — dedupes multiple page_exit rows in analysis
  var bootT0 = Date.now();
  var cartState = sget('mz_cart') || { total: 0, items: 0 };
  var cartT = null;
  function refreshCartState() {
    if (cartT) clearTimeout(cartT);
    cartT = setTimeout(function () {
      try {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            // upsell attribution: sum lines the ATC bridge stamped as upsell adds
            var up = 0, upq = 0, tabs = {};
            (c.items || []).forEach(function (it) {
              var p = it.properties || {};
              if (p._source_type === 'upsell') {
                up += it.final_line_price || 0;
                upq += it.quantity || 0;
                var t = (p._added_from || 'upsell').replace(/^cart-upsell:/, '');
                tabs[t] = (tabs[t] || 0) + Math.round((it.final_line_price || 0) / 100);
              }
            });
            var tabStr = Object.keys(tabs).map(function (k) { return k + ':\u20b9' + tabs[k]; }).join(', ');
            cartState = { total: Math.round((c.total_price || 0) / 100), items: c.item_count || 0, upsell: Math.round(up / 100), upsellQty: upq, upsellTabs: tabStr };
            sset('mz_cart', cartState);
          })
          .catch(function () {});
      } catch (e) {}
    }, 400);
  }
  document.addEventListener('cart:updated', refreshCartState);
  if (!sget('mz_cart')) refreshCartState();
  var IS_BOT = !!navigator.webdriver || /bot|crawl|spider|headless|lighthouse|phantomjs|slurp|bingpreview/i.test(navigator.userAgent || '');

  /* ---------- Geo (city/state/country) — one free HTTPS lookup per session ---------- */
  // Browsers can't read the visitor IP directly; this resolves it once and caches
  // in sessionStorage. Beacons include whatever is cached (first hit may be blank).
  (function initGeo() {
    if (IS_BOT) return;                 // bots and Lighthouse burned the quota
    if (sget('mz_geo')) return;
    /* The old version only cached on SUCCESS. ipwho.is is now rate-limiting us
       (HTTP 429), so nothing was ever cached and every single page view fired
       the request again — a guaranteed failed cross-origin call per pageview,
       for every visitor. Failures are now cached too, so a rate-limited session
       asks once and then stops. Also moved off the load path: geo is only read
       when a beacon is built, so it never needs to race the page. */
    var run = function () {
      try {
        fetch('https://ipwho.is/?fields=success,ip,city,region,country_code')
          .then(function (r) { return r.json(); })
          .then(function (g) {
            if (g && g.success !== false) {
              sset('mz_geo', { ip: g.ip || '', city: g.city || '', region: g.region || '', country: g.country_code || '' });
            } else {
              sset('mz_geo', {});       // negative cache — do not retry this session
            }
          })
          .catch(function () { sset('mz_geo', {}); });
      } catch (e) {}
    };
    if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 5000 });
    else setTimeout(run, 1500);
  })();

  /* ---------- Beacon sink: Google Apps Script web-app (exec URL from settings) ---------- */
  var ENDPOINT = (CTX.trackingEndpoint || '').trim();
  function qpush(b) { try { var arr = lget('mz_q') || []; arr.push(b); if (arr.length > 50) arr = arr.slice(-50); lset('mz_q', arr); } catch (e) {} }
  // Re-send beacons that failed on a previous page (offline, tab killed mid-flight)
  try {
    var pend = lget('mz_q');
    if (pend && pend.length && ENDPOINT && !IS_BOT) {
      lset('mz_q', []);
      pend.forEach(function (b) { try { fetch(ENDPOINT, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: JSON.stringify(b) }).catch(function () { qpush(b); }); } catch (e) {} });
    }
  } catch (e) {}
  function send(event, extra) {
    if (!ENDPOINT || IS_BOT) return; // no exec URL set / bot traffic → skip silently
    var a = attr || {}, l = a.last || {}, f = a.first || {}, g = sget('mz_geo') || {};
    var body = {
      ts: iso(), event: event, vid: visitorId, sid: sessionId, pid: pageId,
      t_s: Math.round((Date.now() - bootT0) / 1000),
      page_num: (typeof journey !== 'undefined' && journey && journey.pages) || 0,
      page_type: CTX.page_type || '', handle: CTX.handle || '', path: location.pathname,
      source: l.utm_source || f.utm_source || 'direct', medium: l.utm_medium || f.utm_medium || '',
      campaign: l.utm_campaign || f.utm_campaign || '',
      cart_value: cartState.total, cart_items: cartState.items,
      visit_num: visitNum, tenure_d: tenureDays,
      device: (window.matchMedia && matchMedia('(max-width:749px)').matches ? 'mobile' : 'desktop'),
      country: g.country || '', region: g.region || '', city: g.city || '',
      customer: CTX.customer_id || '', currency: CTX.currency || ''
    };
    if (extra) for (var k in extra) { if (extra.hasOwnProperty(k)) body[k] = extra[k]; }
    try {
      var blob = new Blob([JSON.stringify(body)], { type: 'text/plain;charset=UTF-8' }); // text/plain avoids CORS preflight to Apps Script
      if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
    } catch (e) {}
    try { fetch(ENDPOINT, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: JSON.stringify(body) }).catch(function () { qpush(body); }); } catch (e) { qpush(body); }
  }

  /* ---------- Attribution (first + last touch) ---------- */
  var UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var CLICK = ['gclid', 'fbclid', 'msclkid', 'ttclid', 'wbraid', 'gbraid'];

  // Collapse the many hostnames/aliases of one channel into a single clean source
  // (m.facebook.com / l.facebook.com / fb → facebook; ig → instagram; google.* → google …)
  var ALIAS = { ig: 'instagram', igshopping: 'instagram', fb: 'facebook', 'facebook.com': 'facebook', 'm.facebook.com': 'facebook', 'l.facebook.com': 'facebook', an: 'facebook', yt: 'youtube' };
  function normSource(s) {
    s = String(s || '').toLowerCase();
    if (ALIAS[s]) return ALIAS[s];
    var h = s.replace(/^(www|m|l|lm|mobile)\./, '');
    if (/(facebook|fb)\.(com|me)/.test(h)) return 'facebook';
    if (/instagram\.com/.test(h)) return 'instagram';
    if (/google\./.test(h)) return 'google';
    if (/youtube\.com|youtu\.be/.test(h)) return 'youtube';
    if (/x\.com|twitter\.com|t\.co/.test(h)) return 'twitter';
    if (/bing\.com/.test(h)) return 'bing';
    if (/pinterest\./.test(h)) return 'pinterest';
    if (/whatsapp|wa\.me/.test(h)) return 'whatsapp';
    if (/duckduckgo/.test(h)) return 'duckduckgo';
    return h || s;
  }
  function buildTouch() {
    var t = { ts: iso(), landing: location.pathname + location.search, referrer: document.referrer || '' };
    UTM.forEach(function (k) { if (q(k)) t[k] = q(k); });
    CLICK.forEach(function (k) { if (q(k)) t[k] = q(k); });
    // Infer paid channel from click-ids when UTM is missing (untagged ad clicks)
    if (!t.utm_campaign) {
      if (q('gclid') || q('gbraid') || q('wbraid')) { t.utm_source = t.utm_source || 'google'; t.utm_medium = t.utm_medium || 'cpc'; }
      else if (q('fbclid')) { t.utm_source = t.utm_source || 'facebook'; t.utm_medium = t.utm_medium || 'paid-social'; }
      else if (q('ttclid')) { t.utm_source = t.utm_source || 'tiktok'; t.utm_medium = t.utm_medium || 'paid-social'; }
    }
    /* Recover UTMs from an INTERNAL referrer's query string: if the tracker's first
       run happens one page after the ad landing (deploy timing, cached page), the
       landing URL — with its full utm_/fbclid payload — is still in document.referrer.
       Without this the visit is misfiled as "direct". */
    if (!t.utm_source && document.referrer) {
      try {
        var ru = new URL(document.referrer);
        if (location.hostname.indexOf(ru.hostname.replace(/^www\./, '')) >= 0 && ru.search) {
          var rq = new URLSearchParams(ru.search);
          UTM.forEach(function (k) { if (!t[k] && rq.get(k)) t[k] = rq.get(k); });
          CLICK.forEach(function (k) { if (!t[k] && rq.get(k)) t[k] = rq.get(k); });
        }
      } catch (e) {}
    }
    if (!t.utm_source) {
      var r = document.referrer;
      if (!r) { t.utm_source = 'direct'; t.utm_medium = t.utm_medium || 'none'; }
      else {
        try {
          var h = new URL(r).hostname.replace(/^www\./, '');
          if (h && location.hostname.indexOf(h) < 0 && !isNonMarketingRef(h)) { t.utm_source = h; t.utm_medium = t.utm_medium || 'referral'; }
          else { t.utm_source = 'direct'; t.utm_medium = t.utm_medium || 'none'; }
        } catch (e) { t.utm_source = 'referral'; }
      }
    }
    t.utm_source = normSource(t.utm_source);
    return t;
  }
  // Payment/checkout/auth round-trips send a referrer but are NOT marketing touches
  function isNonMarketingRef(h) {
    return /payu|razorpay|paytm\.|cashfree|billdesk|phonepe|gokwik|pickrr|fastrr|shiprocket|juspay|ccavenue|instamojo|simpl|snapmint|shopflo|shopify\.com|shop\.app|accounts\.google/.test(h || '');
  }
  function isNewCampaign() {
    if (UTM.some(function (k) { return q(k); })) return true;
    if (CLICK.some(function (k) { return q(k); })) return true;
    var r = document.referrer;
    if (r) {
      try {
        var h = new URL(r).hostname.replace(/^www\./, '');
        if (location.hostname.indexOf(h) < 0 && !isNonMarketingRef(h)) return true;
      } catch (e) {}
    }
    return false;
  }

  var attr = lget(LS);
  if (!attr || !attr.first) {
    var f = buildTouch();
    attr = { first: f, last: f, touch_count: 1, touches: [{ s: f.utm_source, m: f.utm_medium || '', c: f.utm_campaign || '', t: f.ts }] };
    lset(LS, attr);
  } else if (isNewCampaign()) {
    var nt = buildTouch();
    var lt = attr.last || {};
    // Re-clicking the SAME campaign is not a new touch — don't inflate touch_count
    var sameTouch = nt.utm_source === lt.utm_source
      && (nt.utm_medium || '') === (lt.utm_medium || '')
      && (nt.utm_campaign || '') === (lt.utm_campaign || '');
    if (!sameTouch) {
      attr.last = nt;
      attr.touch_count = (attr.touch_count || 1) + 1;
      attr.touches = (attr.touches || []).concat([{ s: nt.utm_source, m: nt.utm_medium || '', c: nt.utm_campaign || '', t: nt.ts }]).slice(-10);
      // A "direct" first-touch carries zero information — if the first REAL campaign
      // arrives while first is still direct, upgrade first to it (deploy-timing rescue).
      if (attr.first && (attr.first.utm_source === 'direct' || !attr.first.utm_source) && attr.last.utm_source && attr.last.utm_source !== 'direct') {
        attr.first = attr.last;
      }
      lset(LS, attr);
    }
  }

  /* ---------- Journey (per session) ---------- */
  var journey = sget(SS) || { started: iso(), steps: [], pages: 0, searches: [] };
  if (!journey.entry) {
    var ent = q('utm_source') ? normSource(q('utm_source')) : '';
    if (!ent && (q('gclid') || q('gbraid') || q('wbraid'))) ent = 'google';
    else if (!ent && q('fbclid')) ent = 'facebook';
    else if (!ent && q('ttclid')) ent = 'tiktok';
    if (!ent && document.referrer) {
      try {
        var eh = new URL(document.referrer).hostname.replace(/^www\./, '');
        if (eh && location.hostname.indexOf(eh) < 0 && !isNonMarketingRef(eh)) ent = normSource(eh);
      } catch (e) {}
    }
    journey.entry = ent || 'direct';
    sset(SS, journey);
  }
  function stepLabel() {
    var pt = CTX.page_type || '';
    if (pt === 'index') return 'home';
    if (pt === 'product') return 'pdp:' + (CTX.handle || '');
    if (pt === 'collection') return 'collection:' + (CTX.handle || '');
    if (pt === 'search') return 'search';
    if (pt === 'cart') return 'cart';
    if (pt === 'page') return 'page:' + (CTX.handle || '');
    return pt || (location.pathname || '/');
  }
  (function logPage() {
    var s = stepLabel();
    if (journey.steps[journey.steps.length - 1] !== s) {
      journey.steps.push(s);
      if (journey.steps.length > 40) journey.steps.shift();
    }
    journey.pages = (journey.pages || 0) + 1;
    var sq = q('q');
    if (CTX.page_type === 'search' && sq) { journey.searches.push(sq); if (journey.searches.length > 20) journey.searches.shift(); }
    sset(SS, journey);
  })();

  /* ---------- Engagement: time on page, scroll depth, click count ---------- */
  var pageT0 = Date.now(), engagedMs = 0, lastAct = Date.now(), maxScroll = 0, clickN = 0;
  var lcpMs = 0, ttfbMs = 0, tabReturns = 0;
  try {
    new PerformanceObserver(function (l) {
      var es = l.getEntries(); if (es.length) lcpMs = es[es.length - 1].startTime;
    }).observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {}
  try {
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav) ttfbMs = nav.responseStart || 0;
  } catch (e) {}
  var secSeen = [], prodSeen = {}, impBatch = [];
  var secTime = {}, secVisible = {};
  setInterval(function () {
    if (document.visibilityState !== 'visible') return;
    for (var k in secVisible) { if (secVisible[k]) secTime[k] = (secTime[k] || 0) + 1; }
  }, 1000);
  ['pointerdown', 'touchstart', 'keydown', 'scroll'].forEach(function (ev) {
    try { document.addEventListener(ev, function () { lastAct = Date.now(); }, { passive: true, capture: true }); } catch (e) {}
  });
  document.addEventListener('click', function () { lastAct = Date.now(); clickN++; }, true);
  function calcScroll() {
    try {
      var d = document.documentElement;
      var p = (window.scrollY + window.innerHeight) / Math.max(1, d.scrollHeight) * 100;
      if (p > maxScroll) maxScroll = Math.min(100, p);
    } catch (e) {}
  }
  try { window.addEventListener('scroll', calcScroll, { passive: true }); } catch (e) {}
  setTimeout(calcScroll, 800);
  // Engaged time = seconds where the tab is visible AND the shopper acted <15s ago
  setInterval(function () { if (document.visibilityState === 'visible' && Date.now() - lastAct < 15000) engagedMs += 1000; }, 1000);

  // Multi-pick milestones belong in the journey trail (and on the order)
  function pushStep(st) {
    if (journey.steps[journey.steps.length - 1] === st) return;
    journey.steps.push(st);
    if (journey.steps.length > 40) journey.steps.shift();
    sset(SS, journey);
  }
  document.addEventListener('basket:open', function () { pushStep('multipick:' + (CTX.handle || 'collection')); });
  document.addEventListener('basket:add', function (e) {
    var d = (e && e.detail) || {};
    pushStep('multipick-add:' + (d.products || d.count || ''));
    try { sset('mz_mp', { items: d.products || 0, packets: d.count || 0, value: d.value || 0 }); } catch (x) {}
  });

  // Section-view tracking: your sections carry [data-track-section]; note the ones seen.
  try {
    if ('IntersectionObserver' in window) {
      var seen = {};
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var nm = e.target.getAttribute('data-track-section');
          if (nm) secVisible[nm] = e.isIntersecting; // dwell accounting either way
          if (!e.isIntersecting) return;
          var name = nm;
          // one 'sec:' step per section name per SESSION — repeated instances and
          // revisits add nothing and would flood the 40-step trail
          if (name && !seen[name]) { seen[name] = 1; secSeen.push(name); var s = 'sec:' + name; if (journey.steps.indexOf(s) === -1) { journey.steps.push(s); if (journey.steps.length > 40) journey.steps.shift(); sset(SS, journey); } }
        });
      }, { threshold: 0.4 });
      document.addEventListener('DOMContentLoaded', function () {
        // Auto-tag EVERY Shopify section wrapper that isn't explicitly tagged, using the
        // section type from its wrapper id — full journey coverage without editing markup.
        // Strip the random per-instance suffix (collection_bundle_DpDgg8 → collection_bundle)
        // and skip meaningless raw ids (1774419374876594bc), or the trail becomes noise.
        document.querySelectorAll('.shopify-section').forEach(function (el) {
          if (el.hasAttribute('data-track-section') || el.querySelector('[data-track-section]')) return;
          var id = el.id || '';
          var m = id.match(/__([a-z0-9_-]+)$/i) || id.match(/^shopify-section-(.+)$/i);
          if (!m || !m[1]) return;
          var name = m[1].replace(/^template--[^_]*__/, '').replace(/_[A-Za-z0-9]{5,8}$/, '');
          if (!name || /^[0-9a-f]{10,}$/i.test(name) || /^\d+$/.test(name)) return;
          // Site chrome is always on screen — "seen" carries no intent, only noise
          if (/^(header|announcement[-_]bar|footer|top[-_]bar|overlay|custom[-_]colors|fake[-_]order|product[-_]quickview|divider|main|cart[-_]drawer|cart[-_]icon[-_]bubble|mobile[-_]bottom[-_]bar|mobile[-_]navigation[-_]bar|back[-_]to[-_]top|sr[-_]checkout)$/.test(name)) return;
          el.setAttribute('data-track-section', name);
        });
        document.querySelectorAll('[data-track-section]').forEach(function (el) { io.observe(el); });
      });
    }
  } catch (e) {}

  /* ---------- Browsing memoir (what they looked at before buying) ---------- */
  (function memoir() {
    try {
      if (CTX.page_type === 'product' && CTX.handle) {
        var pv = lget('mz_pv') || [];
        pv = pv.filter(function (h) { return h !== CTX.handle; });
        pv.unshift(CTX.handle);
        lset('mz_pv', pv.slice(0, 15));
      }
      if (CTX.page_type === 'collection' && CTX.handle) {
        var cv = lget('mz_cv') || [];
        cv = cv.filter(function (h) { return h !== CTX.handle; });
        cv.unshift(CTX.handle);
        lset('mz_cv', cv.slice(0, 10));
      }
    } catch (e) {}
  })();

  /* ---------- Line-item properties for the next add ---------- */
  function props(extra) {
    var p = {}, a = attr || {}, l = a.last || {}, f = a.first || {};
    p._source = l.utm_source || f.utm_source || 'direct';
    if (l.utm_medium || f.utm_medium) p._medium = l.utm_medium || f.utm_medium;
    if (l.utm_campaign || f.utm_campaign) p._campaign = l.utm_campaign || f.utm_campaign;
    p._added_page = location.pathname;
    p._added_at = iso();
    if (extra) { for (var k in extra) { if (extra.hasOwnProperty(k) && extra[k] != null && extra[k] !== '') p[k] = String(extra[k]); } }
    return p;
  }

  /* ---------- Flush attribution + journey to cart attributes ----------
     Lean by design: nothing Shopify already records natively (landing_site,
     referring_site), no empty keys, last-touch only when it differs from
     first. null = actively remove a legacy key from carts that carry it. */
  function attributes() {
    var a = attr || {}, f = a.first || {}, l = a.last || {};
    var out = { 'landing-page': null, 'referrer': null, 'timezone': null }; // purge legacy duplicates
    function put(k, v) { out[k] = (v === undefined || v === null || String(v) === '') ? null : String(v); }
    put('first-source', f.utm_source);
    put('first-medium', f.utm_medium);
    put('first-campaign', f.utm_campaign);
    var moved = (l.utm_source && l.utm_source !== f.utm_source) || (l.utm_campaign && l.utm_campaign !== f.utm_campaign);
    put('last-source', moved ? l.utm_source : null);
    put('last-medium', moved ? l.utm_medium : null);
    put('last-campaign', moved ? l.utm_campaign : null);
    put('gclid', l.gclid || f.gclid);
    put('fbclid', l.fbclid || f.fbclid);
    put('device', window.matchMedia && matchMedia('(max-width:749px)').matches ? 'mobile' : 'desktop');
    put('journey', (journey.steps || []).join(' > '));
    var sq = [];
    (journey.searches || []).forEach(function (x) { if (sq[sq.length - 1] !== x) sq.push(x); });
    put('searches', sq.join(', '));
    put('session-entry', journey.entry); // channel that opened the BUYING session — assist = differs from first-source
    var th = a.touches || [];
    put('touch-history', th.length > 1 ? th.map(function (x) { return x.s + (x.c ? ':' + x.c : ''); }).join(' > ') : null);
    put('touch-count', a.touch_count || 1);
    put('pages-viewed', journey.pages || 0);
    put('products-browsed', (lget('mz_pv') || []).slice(0, 10).join(', '));
    put('collections-browsed', (lget('mz_cv') || []).slice(0, 6).join(', '));
    put('visit-number', visitNum);
    put('customer-tenure-days', tenureDays);
    put('upsell-revenue', cartState.upsell > 0 ? '\u20b9' + cartState.upsell : null);
    put('upsell-items', cartState.upsellQty > 0 ? cartState.upsellQty : null);
    put('upsell-by-tab', cartState.upsellTabs || null);
    put('account-orders-count', CTX.customer_id ? (CTX.customer_orders || 0) : null);
    put('account-customer-since', CTX.customer_id ? CTX.customer_since : null);
    var mp = sget('mz_mp');
    put('multipick-haul', mp ? mp.items + ' items · ₹' + mp.value : null);
    return out;
  }
  var flushT = null;
  function flush() {
    if (flushT) clearTimeout(flushT);
    flushT = setTimeout(function () {
      flushNow();
    }, 600);
  }
  function flushNow() {
    if (flushT) clearTimeout(flushT);
    try {
      fetch('/cart/update.js', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attributes() })
      }).catch(function () {});
    } catch (e) {}
  }
  // Keep order attributes fresh whenever the cart changes (add/remove).
  document.addEventListener('cart:updated', flush);

  /* ---------- Emit for the Web Pixel / analytics bridge ---------- */
  function emit(name, payload) {
    try { document.dispatchEvent(new CustomEvent('mz:track', { detail: { name: name, payload: payload || {}, attributes: attributes() } })); } catch (e) {}
  }

  window.mzTrack = {
    props: props,
    attributes: attributes,
    flush: flush,
    emit: emit,
    send: send,
    vid: visitorId,
    sid: sessionId,
    getAttr: function () { return attr; },
    getJourney: function () { return journey; }
  };

  /* ---------- Auto funnel beacons ---------- */
  // One page_view per page = the funnel backbone (home → collection → pdp → cart).
  var conn = '';
  try { conn = (navigator.connection && navigator.connection.effectiveType) || ''; } catch (e) {}
  send('page_view', {
    step: stepLabel(),
    prev: journey.steps[journey.steps.length - 2] || '',
    vw: window.innerWidth || 0,
    conn: conn,
    lang: (navigator.language || '').slice(0, 8)
  });

  // Checkout start: any checkout button/link (native or Shiprocket proxy).
  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest('[name="checkout"], [data-cp-checkout], .shiprocket-headless button, a[href="/checkout"], a[href*="/checkout"]');
    if (el) { try { emit('checkout_start', {}); } catch (x) {} send('checkout_start', {}); flushNow(); } // freshest journey ON the order
  }, true);

  // Product click: any link to a product — captures interest at the card level
  // (grid / upsell / recommendations), even if the shopper never reaches the PDP.
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!a) return;
    if (a.closest && a.closest('predictive-search, #predictive-search-results')) return; // → search_suggest_click
    var m = (a.getAttribute('href') || '').match(/\/products\/([a-z0-9\-]+)/i);
    if (!m) return;
    var sec = a.closest && a.closest('[data-track-section]');
    var pos = '';
    try {
      var card = a.closest('.card-wrapper, .tsp-card, .ccg-card, .ff-product-item');
      if (card && card.parentElement) {
        var sibs = card.parentElement.children, n = 0;
        for (var i = 0; i < sibs.length; i++) { if (sibs[i].matches && (sibs[i].matches('.card-wrapper, .tsp-card, .ccg-card, .ff-product-item') || sibs[i].querySelector('.card-wrapper, .tsp-card, .ccg-card, .ff-product-item'))) { n++; } if (sibs[i] === card || (sibs[i].contains && sibs[i].contains(card))) { pos = n; break; } }
      }
    } catch (err) {}
    send('product_click', { handle: m[1], from: (sec && sec.getAttribute('data-track-section')) || '', position: pos });
  }, true);

  /* ---------- Named precision hooks: theme-specific controls that generic
     listeners can't see (custom elements, divs) or would mislabel. First match
     wins and claims the click (e.__mzNamed) so generic listeners skip it. */
  (function () {
    function lbl(el) { return ((el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim() || el.className || '').slice(0, 48); }
    function secOf(el) { var x = el.closest && el.closest('[data-track-section]'); return (x && x.getAttribute('data-track-section')) || ''; }
    var HOOKS = [
      ['filter-item, .link-filter[data-href]', function (el) { return ['filter_apply', { handle: ((el.getAttribute('data-href') || '').split('?')[1] || lbl(el)).slice(0, 80) }]; }],
      ['filter-sort', function (el) { return ['sort_change', { handle: el.getAttribute('value') || lbl(el) }]; }],
      ['btn-filter', function () { return ['filter_drawer_open', {}]; }],
      ['facet-clear-all', function () { return ['filter_clear_all', {}]; }],
      ['facet-apply-canvas', function () { return ['filter_apply_submit', {}]; }],
      ['view-mode, grid-mode', function (el) { return ['view_mode_change', { handle: el.getAttribute('data-view') || el.getAttribute('data-grid-mode') || '' }]; }],
      ['button-quickview, select-option[data-url*="product-quickview"]', function (el) { var m = (el.getAttribute('data-url') || '').match(/\/products\/([a-z0-9\-]+)/i); return ['quickview_open', { handle: m ? m[1] : '' }]; }],
      ['button-wishlist', function (el) { return ['wishlist_toggle', { handle: el.getAttribute('data-product-id') || '', source_type: el.getAttribute('data-action') || '' }]; }],
      ['button-compare', function (el) { return ['compare_add', { handle: el.getAttribute('data-product-id') || '' }]; }],
      ['.swiper-button-next, .swiper-button-prev, .swiper-arrow', function (el) { return ['carousel_nav', { handle: (el.className.indexOf('prev') > -1) ? 'prev' : 'next' }]; }],
      ['collapsible-block .collapsible-heading, a.is-nav-tabs', function (el) { return ['pdp_tab_open', { handle: lbl(el) }]; }],
      ['.bundle-add-to-cart, [id^="bundle-atc-"]', function (el) { return ['add_to_cart', { source_type: 'bundle', added_from: secOf(el) }]; }],
      ['.card-add-btn', function (el) { return ['bundle_slot_add', { handle: lbl(el) }]; }],
      ['product-addons.product-addons__share, copy-button', function () { return ['share_open', {}]; }],
      ['product-addons.product-addons__ask-question', function () { return ['ask_question_open', {}]; }],
      ['.edd-check-btn', function () { return ['delivery_check', {}]; }],
      ['zoom-action', function () { return ['image_zoom', {}]; }],
      ['[data-action="toggle-nav"], open-children-toggle, back-menu', function (el) { return ['menu_nav', { handle: lbl(el) || 'toggle' }]; }],
      ['.announcement-bar-close', function () { return ['announcement_dismiss', {}]; }],
      ['.dqc__chip[data-qty], .cqp__chip[data-qty]', function (el) { return ['pack_size_select', { qty: el.getAttribute('data-qty') || '' }]; }],
      ['loadmore-button a, a.loadmore, a.actions-loadmore', function () { return ['load_more', {}]; }],
      ['.btn-checkout-dynamic button, .shopify-payment-button button', function () { return ['express_buy', {}]; }],
      ['.fake-order-close', function () { return ['popup_dismiss', { handle: 'fake-order' }]; }],
      ['.build-reel-card', function (el) { return ['reel_open', { handle: (el.getAttribute('data-reel-idx') || lbl(el)) }]; }],
      ['[data-reels-close]', function () { return ['reel_close', {}]; }],
      ['[data-reels-mute]', function () { return ['reel_mute', {}]; }],
      ['.ff-tick', function (el) { return ['interaction', { handle: 'ff-select-toggle' }]; }],
      ['summary', function (el) { var d = el.closest('details'); return ['accordion_toggle', { handle: lbl(el), collapsed: d && d.hasAttribute('open') ? '1' : '0' }]; }]
    ];
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      for (var i = 0; i < HOOKS.length; i++) {
        var el;
        try { el = e.target.closest(HOOKS[i][0]); } catch (err) { continue; }
        if (!el) continue;
        try {
          var r = HOOKS[i][1](el), x = r[1] || {};
          if (!x.from) x.from = secOf(el);
          send(r[0], x);
          e.__mzNamed = true;
        } catch (err) {}
        return;
      }
    }, true);
    // change-driven controls: price sliders, custom pack qty, selects
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || !el.matches) return;
      try {
        if (el.matches('.cqp__custom, .dqc__custom')) { send('pack_size_select', { qty: el.value || '', from: secOf(el) }); return; }
        if (el.matches('input[type="range"], .min-price-range, .max-price-range')) { send('filter_apply', { handle: ('price:' + (el.value || '')).slice(0, 40), from: secOf(el) }); return; }
        if (el.matches('select')) { send('interaction', { handle: ('select:' + (el.name || el.id || '') + '=' + (el.value || '')).slice(0, 48), source_type: secOf(el) }); }
      } catch (err) {}
    }, true);
  })();

  // Universal interaction: EVERY button / CTA / qty-chip / tab / labelled control.
  // The label lands in the 'handle' field (so it aggregates like a dimension), the
  // section in 'source_type'. Product links, add-to-cart and checkout are already
  // beaconed as their own events, so they're skipped here to avoid double counting.
  document.addEventListener('click', function (e) {
    if (e.__mzNamed) return; // a precision hook already named this click
    var el = e.target.closest && e.target.closest(
      'button, [role="button"], input[type="submit"], .tsp-chip, .ccg-chip, .dqc__chip, .cqp__chip, .tsp-tab, .tsp-pack, [data-mz-track]'
    );
    if (!el) return;
    if (el.closest('a[href*="/products/"]')) return;                    // product_click
    if (el.matches('[name="checkout"], [data-cp-checkout]')) return;    // checkout_start
    if (el.matches('[data-tsp-add], [data-ccg-add], [name="add"], .tsp-cta, .ccg-cta, [data-mz-sticky-add]')) return; // add_to_cart
    var label = (el.getAttribute('data-mz-track') || el.getAttribute('aria-label')
      || (el.textContent || '').replace(/\s+/g, ' ').trim() || el.className || 'button').slice(0, 48);
    var sec = el.closest && el.closest('[data-track-section]');
    send('interaction', { handle: label, source_type: (sec && sec.getAttribute('data-track-section')) || '' });
  }, true);

  /* ---------- Link clicks (nav / footer / external) ---------- */
  document.addEventListener('click', function (e) {
    if (e.__mzNamed) return; // a precision hook already named this click
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (/\/products\//.test(href)) return;            // product_click
    if (/checkout/.test(href)) return;                 // checkout_start
    if (href.charAt(0) === '#' || /^javascript:/i.test(href)) return;
    var u = null, ext = false;
    try { u = new URL(href, location.href); ext = u.hostname !== location.hostname; } catch (err) {}
    var sec = a.closest && a.closest('[data-track-section]');
    send('link_click', {
      handle: (ext ? (u && u.hostname) || href : (u && u.pathname) || href).slice(0, 80),
      from: (sec && sec.getAttribute('data-track-section')) || '',
      source_type: ext ? 'external' : 'internal'
    });
  }, true);

  /* ---------- Product impressions (batched) — enables card CTR ---------- */
  function flushImpressions() {
    if (!impBatch.length) return;
    var take = impBatch.splice(0, 24), hs = [], ps = [];
    take.forEach(function (x) { hs.push(x.h); ps.push(x.p); });
    send('product_impression', { handles: hs.join('|'), positions: ps.join('|') });
    if (impBatch.length) setTimeout(flushImpressions, 300);
  }
  var impT = null;
  function schedImp() { if (impT) clearTimeout(impT); impT = setTimeout(flushImpressions, 2500); }
  try {
    if ('IntersectionObserver' in window) {
      var pio = new IntersectionObserver(function (es) {
        es.forEach(function (en) {
          if (!en.isIntersecting) return;
          var card = en.target;
          pio.unobserve(card);
          var a2 = card.querySelector('a[href*="/products/"]');
          if (!a2) return;
          var m2 = (a2.getAttribute('href') || '').match(/\/products\/([a-z0-9\-]+)/i);
          if (!m2 || prodSeen[m2[1]]) return;
          var pos = 1;
          try { var sibs = card.parentElement.children; for (var i = 0; i < sibs.length; i++) { if (sibs[i] === card) { pos = i + 1; break; } } } catch (err) {}
          prodSeen[m2[1]] = 1;
          impBatch.push({ h: m2[1], p: pos });
          schedImp();
        });
      }, { threshold: 0.5 });
      var scanCards = function () { document.querySelectorAll('.card-wrapper, .tsp-card, .ccg-card, .ff-product-item').forEach(function (c) { if (!c.__mzImp) { c.__mzImp = 1; pio.observe(c); } }); };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scanCards); else scanCards();
      if ('MutationObserver' in window) {
        var scanT = null;
        new MutationObserver(function () { clearTimeout(scanT); scanT = setTimeout(scanCards, 400); }).observe(document.documentElement, { childList: true, subtree: true });
      }
    }
  } catch (e) {}

  /* ---------- Search: results count (zero-result = lost demand) + suggestion clicks ---------- */
  if (CTX.page_type === 'search') {
    var countResults = function () { send('search_results', { query: q('q'), count: document.querySelectorAll('.card-wrapper, .tsp-card, .ccg-card, .ff-product-item').length }); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', countResults); else setTimeout(countResults, 300);
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('predictive-search a, #predictive-search-results a');
    if (!a) return;
    var m = (a.getAttribute('href') || '').match(/\/products\/([a-z0-9\-]+)/i);
    var inp = document.querySelector('predictive-search input[type="search"], .search__input');
    send('search_suggest_click', { query: (inp && inp.value) || '', handle: m ? m[1] : (a.getAttribute('href') || '').slice(0, 80) });
  }, true);

  /* ---------- Cart micro-funnel: mirror the drawer/dock DOM events as beacons ---------- */
  var EVMAP = {
    'cart-drawer:open': 'drawer_open', 'cart-drawer:close': 'drawer_close',
    'cart:gift-added': 'gift_unlocked', 'cart:gift-removed': 'gift_removed',
    'cart:item-removed': 'cart_item_removed', 'cart:cleared': 'cart_cleared',
    'cart:upsell-toggled': 'upsell_toggle', 'cart-dock:view-cart': 'dock_view_cart',
    'cart-dock:shown': 'dock_shown', 'search:open': 'search_open',
    /* multi-pick gallery funnel (basket:add beacons itself — mapping it here would double-count) */
    'basket:open': 'basket_open', 'basket:close': 'basket_close',
    'basket:select': 'basket_select', 'basket:deselect': 'basket_deselect',
    'basket:quickpick': 'basket_quickpick', 'basket:loaded': 'basket_loaded',
    'basket:columns': 'basket_columns', 'basket:preview': 'basket_preview',
    'checkout:slide-complete': 'checkout_slide', 'search:zero': 'search_zero',
    /* Mood board studio (/collections/all?view=mood-board) */
    'mood:board-new': 'mood_board_new', 'mood:board-open': 'mood_board_open',
    'mood:item-add': 'mood_item_add', 'mood:picture-save': 'mood_picture_save',
    'mood:share-open': 'mood_share_open', 'mood:ask-share': 'mood_ask_share',
    'mood:cart-add': 'mood_cart_add',
    /* Collage widget (home + collection) */
    'collage:item-add': 'collage_item_add', 'collage:tool': 'collage_tool',
    'collage:picture-save': 'collage_picture_save', 'collage:cart-add': 'collage_cart_add',
    /* Swipe to match */
    'swipe:start': 'swipe_start', 'swipe:like': 'swipe_like', 'swipe:skip': 'swipe_skip',
    'swipe:restart': 'swipe_restart', 'swipe:wishlist-open': 'swipe_wishlist_open',
    /* Wishlist */
    'wish:add': 'wish_add', 'wish:remove': 'wish_remove', 'wish:cart-add': 'wish_cart_add',
    /* Glow toggle + animal facts */
    'glow:toggle': 'glow_toggle', 'glow:help': 'glow_help', 'fact:open': 'fact_open',
    /* Campus crew page */
    'campus:cta': 'campus_cta', 'campus:form-view': 'campus_form_view',
    'campus:form-ready': 'campus_form_ready', 'campus:faq': 'campus_faq'
  };
  Object.keys(EVMAP).forEach(function (k) {
    document.addEventListener(k, function (e) {
      var d = (e && e.detail) || {};
      send(EVMAP[k], {
        tier: d.tier || '', variant: d.variantId || d.variant || '', key: d.key || '',
        qty: d.qty || '', handle: d.handle || '',
        count: d.count || d.n || d.cards || d.products || d.cols || '',
        value: d.value || '',
        collapsed: d.collapsed === true ? '1' : d.collapsed === false ? '0' : '',
        from: d.trigger || d.source || d.via || ''
      });
    });
  });

  /* ---------- Frustration signals: rage clicks + disabled-control taps ---------- */
  (function () {
    var buf = [];
    document.addEventListener('click', function (e) {
      var now = Date.now();
      buf.push({ x: e.clientX, y: e.clientY, t: now });
      buf = buf.filter(function (c) { return now - c.t < 1200; });
      if (buf.length >= 3) {
        var r = buf.every(function (c) { return Math.abs(c.x - buf[0].x) < 32 && Math.abs(c.y - buf[0].y) < 32; });
        if (r) {
          buf = [];
          var el = e.target.closest && e.target.closest('button, a, [role="button"]');
          var label = el ? ((el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ').trim() || el.className).slice(0, 48) : (e.target.tagName || '').toLowerCase();
          var sec = e.target.closest && e.target.closest('[data-track-section]');
          send('rage_click', { handle: label, from: (sec && sec.getAttribute('data-track-section')) || '', position: Math.round(e.clientY) });
        }
      }
      var dis = e.target.closest && e.target.closest('button[disabled], [aria-disabled="true"]');
      if (dis) {
        send('dead_click', { handle: ((dis.getAttribute('aria-label') || dis.textContent || '').replace(/\s+/g, ' ').trim() || 'disabled').slice(0, 48) });
      }
    }, true);
  })();

  /* ---------- Broken UX = silent revenue loss: JS errors (capped 3/page) ---------- */
  (function () {
    var errN = 0;
    window.addEventListener('error', function (e) {
      if (errN >= 3) return; errN++;
      send('js_error', { handle: String(e.message || 'error').slice(0, 100), from: (String(e.filename || '').split('/').pop() + ':' + (e.lineno || 0)).slice(0, 60) });
    });
    window.addEventListener('unhandledrejection', function (e) {
      if (errN >= 3) return; errN++;
      var m = ''; try { m = String((e.reason && (e.reason.message || e.reason)) || 'rejection'); } catch (x) { m = 'rejection'; }
      send('js_error', { handle: m.slice(0, 100), from: 'promise' });
    });
  })();

  /* ---------- Purchase-intent micro-signals ---------- */
  // Variant selections on PDP (which options they touch, even without buying)
  try {
    if (window.subscribe && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (d) {
        var v = '';
        try { v = (d && d.data && d.data.variant && d.data.variant.id) || (d && d.variant && d.variant.id) || ''; } catch (e) {}
        send('variant_select', { variant: v });
      });
    }
  } catch (e) {}
  // Clicks on sold-out cards → ranked restock-demand list
  document.addEventListener('click', function (e) {
    var card = e.target.closest && e.target.closest('[data-mz-oos]');
    if (!card) return;
    var a = card.querySelector('a[href*="/products/"]');
    var m = a && (a.getAttribute('href') || '').match(/\/products\/([a-z0-9\-]+)/i);
    if (m) send('oos_click', { handle: m[1] });
  }, true);
  // Email capture from any newsletter/popup form (event only — never the address)
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') return;
    var act = (form.getAttribute('action') || '');
    if (/cart|checkout|search/.test(act)) return;
    if (!form.querySelector('input[type="email"]')) return;
    var sec = form.closest && form.closest('[data-track-section]');
    send('email_signup', { from: (sec && sec.getAttribute('data-track-section')) || act.slice(0, 40) });
  }, true);

  /* ---------- Page exit: landing→exit trail, dwell, scroll, bounce fuel ----------
     Last page_exit of a session = the leave page. pid dedupes re-sends after the
     tab returns from background (keep the LAST row per pid when analysing). */
  var exitSent = false;
  function sendExit() {
    if (exitSent) return;
    exitSent = true;
    try { flushImpressions(); } catch (e) {}
    send('page_exit', {
      step: stepLabel(),
      engaged_s: Math.round(engagedMs / 1000),
      duration_s: Math.round((Date.now() - pageT0) / 1000),
      scroll_pct: Math.round(maxScroll),
      clicks: clickN,
      sections: Object.keys(secTime).slice(0, 20).map(function (k) { return k + ':' + secTime[k]; }).join('|') || secSeen.slice(0, 20).join('|'),
      products_seen: Object.keys(prodSeen).length,
      lcp_ms: Math.round(lcpMs || 0),
      ttfb_ms: Math.round(ttfbMs || 0),
      returns: tabReturns
    });
  }
  window.addEventListener('pagehide', sendExit);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') sendExit();
    else { exitSent = false; tabReturns++; } // tab came back — a later exit sends updated numbers (same pid)
  });
})();
